import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Panel } from "primereact/panel";
import { useCallback, useContext, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import FormNumberInput from "../../../components/FormNumberInput";
import NoInfo from "../../../components/NoInfo";
import PrimePanel from "../../../components/PrimePanel";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import NumberUtil from "../../../utility/NumberUtil";
import { F106030901Before, queryCbamProcess, updateCbamProcess } from "../../../utility/service/cbam-service";
import { getCBAMSubItemsByItemNo } from "../../../utility/service/companyOffering-service";
import {
  Base64Encoder,
  BcryptEncoder,
  checkDpSuccess,
  getFormErrorMessage,
} from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";
import { Badge } from "primereact/badge";
import { BigNumber } from "bignumber.js";

const ProcessInfo = ({ setActiveIndex }) => {
  const { t } = useTranslation();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const {
    action,
    setAction,
    currentCompany,
    setCurrentCompany,
    currentCbamMasterSeq,
    setCurrentCbamMasterSeq,
  } = useOutletContext();
  const [formRule, setFormRule] = useState([]); //表單驗證 設備
  const [oriData, setOriData] = useState({});
  const [coefficientSourceList, setCoefficientSourceList] = useState([]);
  const isEditable = useEditableFlag("F10603");
  const [tabAction, setTabAction] = useState("detail");
  const editFlag = tabAction === "detail" || !isEditable;

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    handleSubmit,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  const { fields: cbamEminstProcessesFields } = useFieldArray({
    control,
    name: "cbamEminstProcessesList",
  });

  useEffect(() => {
    axios_F106030901Before();
    axios_queryCbamProcess();
    axios_getCBAMSubItemsByItemNo("COEFFIC_SOURCE", 16).then(setCoefficientSourceList);
  }, []);

  const axios_getCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  // D_Processes 表 查詢
  const axios_queryCbamProcess = () => {
    checkLoading(loading);
    queryCbamProcess({
      companySeq: currentCompany?.companySeq,
      cbamMasterSeq: currentCbamMasterSeq,
    })
      .then((res) => {
        if (checkDpSuccess(res)) {
          const oriData = {
            cbamEminstProcessesList: res?.data.RespBody?.cbamEminstProcessesList.map((item, index) => {
              return {
                ...item,
                indirectEmissionsElectricityFlag: item?.indirectEmissionsElectricityFlag || "Y",
                cbamEminstProcessesdList: (item?.cbamEminstProcessesdList ?? []).map((d) => ({
                  ...d,
                  routeName: d.routeId === "all" || !d.routeName ? t("allProduct") : d.routeName,
                })),
              };
            }),
          };

          setOriData(oriData);
          Object.entries(oriData).forEach(([key, value]) => {
            setValue(key, value);
          });
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const setValidProcessesForm = useCallback(
    (formId) => {
      if (setValidForm("cbamEminstProcessesList")?.length > 0) {
        const tmp = setValidForm("cbamEminstProcessesList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamEminstProcessesList")],
  );

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item?.subObject?.value) {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  const axios_F106030901Before = () => {
    checkLoading(loading);
    F106030901Before()
      .then((res) => {
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const nonEditableCell = (value, type = "text") => {
    const safeValue = value ?? "";
    const valueDisplay = type === "number" ? NumberUtil.formatWithCommas(safeValue) || "" : safeValue;
    return <div className={`non-editable-cell ${type === "number" ? "right" : ""}`}>{valueDisplay}</div>;
  };

  const encoder = (dataList, code) => {
    const findItemIndex = (map, code) => {
      return map.get(code) ?? false;
    };
    if (code === null || code === undefined || code === "") return "";
    if (dataList === null || dataList === undefined || dataList === "" || dataList.length <= 0) return "";
    return Base64Encoder(BcryptEncoder(code ?? "")) + "," + findItemIndex(dataList, code ?? "");
  };

  const onSubmit = (data) => {
    checkLoading(loading);
    const coefficientSourceMap = new Map(coefficientSourceList.map((v, i) => [v.subitemNo, v.index]));

    const reqBody = {
      cbamEminstProcessesList: data.cbamEminstProcessesList.map((item) => ({
        ...item,
        coefficientSource: encoder(coefficientSourceMap, item.coefficientSource),
        cbamMasterSeq: item.currentCbamMasterSeq || currentCbamMasterSeq,
      })),
    };
    updateCbamProcess(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          setActiveIndex(5);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const HeaderTemplate = ({ options, field, index }) => {
    return (
      <div className={`${options.className}`} key={field.cbamDetailSeq}>
        <div className="flex w-full font-bold align-items-center">
          <Badge value={index + 1} style={{ marginRight: "0.5rem" }} />
          {t("productionProcess")} {field.displayName ? " : " + field.displayName : ""}
        </div>
        {options.togglerElement}
      </div>
    );
  };

  return (
    <>
      {cbamEminstProcessesFields.length === 0 && <NoInfo />}
      <form className="grid" onSubmit={handleSubmit(onSubmit)}>
        {cbamEminstProcessesFields.map((field, index) => (
          <div key={field.id} className="col-12">
            <PrimePanel
              key={field.id}
              toggleable
              className="evaluation-temp-setting-panel w-full"
              headerTemplate={(options) => <HeaderTemplate options={options} field={field} index={index} />}
            >
              <div key={field.id} className="col-12 grid">
                <div className="col-12">
                  <DataTable
                    scrollable
                    scrollHeight="35rem"
                    stripedRows
                    resizableColumns
                    {...(!editFlag
                      ? {
                          editMode: "cell",
                        }
                      : {})}
                    className={"white-data-table hover-highlight no-shadow"}
                    value={getValues(`cbamEminstProcessesList.${index}.cbamEminstProcessesdList`)}
                    emptyMessage={<NoInfo panel="table" />}
                  >
                    <Column header={t("productionRoute")} field={"routeName"} />
                    <Column
                      header={t("totalProduction")}
                      field={"totalProduct"}
                      body={(rowData, options) =>
                        editFlag ? (
                          <div className="text-right">{rowData.totalProduct}</div>
                        ) : (
                          <FormNumberInput
                            disabled={editFlag}
                            register={register}
                            errors={errors}
                            id={`cbamEminstProcessesList.${index}.cbamEminstProcessesdList.${options.rowIndex}.totalProduct`}
                            name={`cbamEminstProcessesList.${index}.cbamEminstProcessesdList.${options.rowIndex}.totalProduct`}
                            value={watch(
                              `cbamEminstProcessesList.${index}.cbamEminstProcessesdList.${options.rowIndex}.totalProduct`,
                            )}
                            onChange={(newValue) =>
                              setValue(
                                `cbamEminstProcessesList.${index}.cbamEminstProcessesdList.${options.rowIndex}.totalProduct`,
                                newValue,
                              )
                            }
                          />
                        )
                      }
                    />
                    <Column header={t("unit")} field={"unit"} body={(rowData) => nonEditableCell("t")} />
                    {/* <Column header={t("euShipment")} body={(rowData) => nonEditableCell("")} />
                    <Column header={t("shipmentPercentage")} body={(rowData) => nonEditableCell("")} />
                    <Column header={t("otherProcessShipment")} body={(rowData) => nonEditableCell("")} />
                    <Column header={t("nonCbamProductShipment")} body={(rowData) => nonEditableCell("")} /> */}
                  </DataTable>
                </div>
                {/* 設施內總產量 */}
                <div className="col-12 lg:col-4">
                  <FormNumberInput
                    disabled={true}
                    register={register}
                    errors={errors}
                    id={`cbamEminstProcessesList.${index}.totalWeight`}
                    name={`cbamEminstProcessesList.${index}.totalWeight`}
                    labelText={t("totalProductionInsideFacility")}
                    value={(() => {
                      const totalList = watch(`cbamEminstProcessesList.${index}.cbamEminstProcessesdList`) || [];
                      const sum = totalList.reduce(
                        (acc, item) => acc.plus(new BigNumber(item.totalProduct || 0)),
                        new BigNumber(0),
                      );
                      return sum.toString();
                    })()}
                  />
                </div>
                {/* 直接歸屬排放 */}
                <div className="col-12 lg:col-4">
                  <FormNumberInput
                    disabled={editFlag}
                    register={register}
                    errors={errors}
                    id={`cbamEminstProcessesList.${index}.directlyAttributableEmissions`}
                    name={`cbamEminstProcessesList.${index}.directlyAttributableEmissions`}
                    labelText={`${t("directAttributableEmissions")}(DirEm)(tCO2e)`}
                    labelClass="required"
                    rules={setValidProcessesForm("directlyAttributableEmissions")}
                    value={watch(`cbamEminstProcessesList.${index}.directlyAttributableEmissions`)}
                    onChange={(newValue) => {
                      setValue(`cbamEminstProcessesList.${index}.directlyAttributableEmissions`, newValue);
                    }}
                  />
                </div>
                {/* 係數來源 */}
                <div className="col-12 lg:col-4">
                  <label className="form-label required">{t("coefficientSource")}</label>
                  <Controller
                    name={`cbamEminstProcessesList.${index}.coefficientSource`}
                    control={control}
                    rules={setValidProcessesForm("coefficientSource")}
                    render={({ field, fieldState }) => (
                      <>
                        <Dropdown
                          disabled={editFlag}
                          filter
                          showClear
                          appendTo={"self"}
                          optionLabel="subitemName"
                          optionValue="subitemNo"
                          value={field.value}
                          virtualScrollerOptions={{ itemSize: 40 }}
                          defaultValue={""}
                          placeholder={t("plz_chs")}
                          control={control}
                          options={coefficientSourceList}
                          className={"w-full"}
                          onChange={(event) => {
                            field.onChange(event.value);
                          }}
                        ></Dropdown>
                        {fieldState.error && <small className="p-error block mt-1">{fieldState.error.message}</small>}
                      </>
                    )}
                  ></Controller>
                </div>
                {/* 間接能源使用 */}
                <div className="col-12">
                  <label className="font-semibold">{t("indirectEnergyUsage")}</label>
                </div>
                {/* 間接排放 */}
                <div className="col-12 lg:col-4">
                  <Panel
                    header={
                      <>
                        <Controller
                          name={`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`}
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              inputId={`indirectEmissionsElectricityFlag-${index}`}
                              disabled={editFlag}
                              checked={field.value === "Y"}
                              onChange={(e) => {
                                field.onChange(e.checked ? "Y" : "N");
                                if (!e.checked) {
                                  setValue(`cbamEminstProcessesList.${index}.electricityConsumption`, "");
                                  setValue(`cbamEminstProcessesList.${index}.electricityEmissionCoefficient`, "");
                                  setValue(`cbamEminstProcessesList.${index}.electricityOutput`, "");
                                  setValue(`cbamEminstProcessesList.${index}.electricityOutputCoefficient`, "");
                                }
                              }}
                            />
                          )}
                        />
                        <label htmlFor={`indirectEmissionsElectricityFlag-${index}`} className="font-semibold ml-2">
                          {t("indirectEmissions")}
                        </label>
                      </>
                    }
                  >
                    {/* 耗電量 MWh */}
                    <FormNumberInput
                      disabled={
                        editFlag || watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) !== "Y"
                      }
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.electricityConsumption`}
                      name={`cbamEminstProcessesList.${index}.electricityConsumption`}
                      labelText={`${t("electricityConsumption")}(MWh)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) === "Y"
                          ? "required"
                          : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.electricityConsumption`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.electricityConsumption`, newValue);
                      }}
                    />
                    {/* 電力排放係數 tCO2/Mwh */}
                    <FormNumberInput
                      disabled={
                        editFlag || watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) !== "Y"
                      }
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.electricityEmissionCoefficient`}
                      name={`cbamEminstProcessesList.${index}.electricityEmissionCoefficient`}
                      labelText={`${t("electricityEmissionCoefficient")}(tCO2/Mwh)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) === "Y"
                          ? "required"
                          : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.electricityEmissionCoefficient`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.electricityEmissionCoefficient`, newValue);
                      }}
                    />
                    {/* 產電量 MWh */}
                    <FormNumberInput
                      disabled={
                        editFlag || watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) !== "Y"
                      }
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.electricityOutput`}
                      name={`cbamEminstProcessesList.${index}.electricityOutput`}
                      labelText={`${t("electricityOutput")}(MWh)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) === "Y"
                          ? "required"
                          : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.electricityOutput`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.electricityOutput`, newValue);
                      }}
                    />
                    {/* 電力產出係數 tCO2/Mwh */}
                    <FormNumberInput
                      disabled={
                        editFlag || watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) !== "Y"
                      }
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.electricityOutputCoefficient`}
                      name={`cbamEminstProcessesList.${index}.electricityOutputCoefficient`}
                      labelText={`${t("electricityOutputCoefficient")}(tCO2/Mwh)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) === "Y"
                          ? "required"
                          : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.indirectEmissionsElectricityFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.electricityOutputCoefficient`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.electricityOutputCoefficient`, newValue);
                      }}
                    />
                  </Panel>
                </div>
                {/* 熱能 */}
                <div className="col-12 lg:col-4">
                  <Panel
                    header={
                      <>
                        <Controller
                          name={`cbamEminstProcessesList.${index}.measurableHeatFlag`}
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              inputId={`measurableHeatFlag-${index}`}
                              disabled={editFlag}
                              checked={field.value === "Y"}
                              onChange={(e) => {
                                field.onChange(e.checked ? "Y" : "N");
                                if (!e.checked) {
                                  setValue(`cbamEminstProcessesList.${index}.measurableHeatInput`, "");
                                  setValue(`cbamEminstProcessesList.${index}.measurableHeatEmissionCoefficient`, "");
                                  setValue(`cbamEminstProcessesList.${index}.measurableHeatOutput`, "");
                                  setValue(`cbamEminstProcessesList.${index}.measurableHeatOutputCoefficient`, "");
                                }
                              }}
                            />
                          )}
                        />
                        <label htmlFor={`measurableHeatFlag-${index}`} className="font-semibold ml-2">
                          {t("heatEnergy")}
                        </label>
                      </>
                    }
                  >
                    {/* 熱能導入 TJ */}
                    <FormNumberInput
                      disabled={editFlag || watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) !== "Y"}
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.measurableHeatInput`}
                      name={`cbamEminstProcessesList.${index}.measurableHeatInput`}
                      labelText={`${t("heatInput")}(TJ)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) === "Y" ? "required" : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.measurableHeatInput`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.measurableHeatInput`, newValue);
                      }}
                    />
                    {/* 熱能排放係數 tCO2/TJ */}
                    <FormNumberInput
                      disabled={editFlag || watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) !== "Y"}
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.measurableHeatEmissionCoefficient`}
                      name={`cbamEminstProcessesList.${index}.measurableHeatEmissionCoefficient`}
                      labelText={`${t("heatEmissionCoefficient")}(tCO2/TJ)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) === "Y" ? "required" : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.measurableHeatEmissionCoefficient`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.measurableHeatEmissionCoefficient`, newValue);
                      }}
                    />
                    {/* 熱能產出 TJ */}
                    <FormNumberInput
                      disabled={editFlag || watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) !== "Y"}
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.measurableHeatOutput`}
                      name={`cbamEminstProcessesList.${index}.measurableHeatOutput`}
                      labelText={`${t("heatOutput")}(TJ)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) === "Y" ? "required" : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.measurableHeatOutput`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.measurableHeatOutput`, newValue);
                      }}
                    />
                    {/* 熱能產出係數 tCO2/TJ */}
                    <FormNumberInput
                      disabled={editFlag || watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) !== "Y"}
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.measurableHeatOutputCoefficient`}
                      name={`cbamEminstProcessesList.${index}.measurableHeatOutputCoefficient`}
                      labelText={`${t("heatOutputCoefficient")}(tCO2/TJ)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) === "Y" ? "required" : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.measurableHeatFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.measurableHeatOutputCoefficient`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.measurableHeatOutputCoefficient`, newValue);
                      }}
                    />
                  </Panel>
                </div>
                {/* 廢氣 */}
                <div className="col-12 lg:col-4">
                  <Panel
                    header={
                      <>
                        <Controller
                          name={`cbamEminstProcessesList.${index}.wasteGasesFlag`}
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              inputId={`wasteGasesFlag-${index}`}
                              disabled={editFlag}
                              checked={field.value === "Y"}
                              onChange={(e) => {
                                field.onChange(e.checked ? "Y" : "N");
                                if (!e.checked) {
                                  setValue(`cbamEminstProcessesList.${index}.wasteGasesInput`, "");
                                  setValue(`cbamEminstProcessesList.${index}.wasteGasesEmissionCoefficient`, "");
                                  setValue(`cbamEminstProcessesList.${index}.wasteGasesOutput`, "");
                                  setValue(`cbamEminstProcessesList.${index}.wasteGasesOutputCoefficient`, "");
                                }
                              }}
                            />
                          )}
                        />
                        <label htmlFor={`wasteGasesFlag-${index}`} className="font-semibold ml-2">
                          {t("wasteGas")}
                        </label>
                      </>
                    }
                  >
                    {/* 廢氣導入 TJ */}
                    <FormNumberInput
                      disabled={editFlag || watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) !== "Y"}
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.wasteGasesInput`}
                      name={`cbamEminstProcessesList.${index}.wasteGasesInput`}
                      labelText={`${t("wasteGasInput")}(TJ)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) === "Y" ? "required" : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.wasteGasesInput`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.wasteGasesInput`, newValue);
                      }}
                    />
                    {/* 廢氣排放係數 tCO2/TJ */}
                    <FormNumberInput
                      disabled={editFlag || watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) !== "Y"}
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.wasteGasesEmissionCoefficient`}
                      name={`cbamEminstProcessesList.${index}.wasteGasesEmissionCoefficient`}
                      labelText={`${t("wasteGasEmissionCoefficient")}(tCO2/TJ)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) === "Y" ? "required" : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.wasteGasesEmissionCoefficient`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.wasteGasesEmissionCoefficient`, newValue);
                      }}
                    />
                    {/* 廢棄產出 TJ */}
                    <FormNumberInput
                      disabled={editFlag || watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) !== "Y"}
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.wasteGasesOutput`}
                      name={`cbamEminstProcessesList.${index}.wasteGasesOutput`}
                      labelText={`${t("wasteGasOutput")}(TJ)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) === "Y" ? "required" : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.wasteGasesOutput`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.wasteGasesOutput`, newValue);
                      }}
                    />
                    {/* 廢棄產出係數 tCO2/TJ */}
                    <FormNumberInput
                      disabled={editFlag || watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) !== "Y"}
                      register={register}
                      errors={errors}
                      id={`cbamEminstProcessesList.${index}.wasteGasesOutputCoefficient`}
                      name={`cbamEminstProcessesList.${index}.wasteGasesOutputCoefficient`}
                      labelText={`${t("wasteGasOutputCoefficient")}(tCO2/TJ)`}
                      labelClass={`${
                        watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) === "Y" ? "required" : ""
                      }`}
                      rules={{
                        required: {
                          value: watch(`cbamEminstProcessesList.${index}.wasteGasesFlag`) === "Y",
                          message: t("validation.required"),
                        },
                      }}
                      value={watch(`cbamEminstProcessesList.${index}.wasteGasesOutputCoefficient`)}
                      onChange={(newValue) => {
                        setValue(`cbamEminstProcessesList.${index}.wasteGasesOutputCoefficient`, newValue);
                      }}
                    />
                  </Panel>
                </div>
              </div>
            </PrimePanel>
          </div>
        ))}

        {cbamEminstProcessesFields.length > 0 && (
          <div className="col-12 gap-2 flex-all-center">
            {tabAction === "detail" && (
              <Button
                disabled={!isEditable}
                type="button"
                label={t("button.edit")}
                size="small"
                onClick={() => {
                  setTabAction("create");
                }}
              />
            )}
            {tabAction === "create" && (
              <>
                <Button
                  disabled={!(isValid && isDirty)}
                  type="submit"
                  label={action === "create" ? t("button.create") : t("button.update")}
                  size="small"
                />
                {action !== "create" && (
                  <Button
                    outlined
                    size="small"
                    label={t("button.cancel")}
                    onClick={(e) => {
                      e.preventDefault();
                      clearErrors();
                      setTabAction("detail");
                      reset(oriData);
                    }}
                  />
                )}
              </>
            )}
          </div>
        )}
      </form>
    </>
  );
};

export default ProcessInfo;
